import { type Dispatch, type SetStateAction, useCallback } from "react";
import { emitter } from "src/state/events.ts";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import type {
  UseQueryResult,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import type { TFunction } from "i18next";

export const useResourceFormRefresh = <
  FormValues extends FieldValues = FieldValues,
>({
  resource,
  id,
  action,
  clearStorage,
  form,
  initialValues,
  processResult,
  refetch,
  refetchTranslations,
  refetchFormConfig,
  setRefreshCount,
  t,
}: {
  resource: string;
  form: UseFormReturn<FormValues>;
  initialValues: FormValues;
  id: string | undefined;
  action: "edit" | "create";
  refetch: UseQueryResult<any>["refetch"];
  refetchTranslations: UseSuspenseQueryResult<any>["refetch"];
  refetchFormConfig: UseSuspenseQueryResult<any>["refetch"];
  clearStorage: () => void;
  setRefreshCount: Dispatch<SetStateAction<number>>;
  t: TFunction;
  processResult: (result: UseQueryResult) => void;
}) => {
  return useCallback(
    async (
      params: {
        resetForm?: boolean;
        revalidateData?: boolean;
      } = {},
    ) => {
      const { resetForm = true, revalidateData = true } = params;
      if (!resetForm && !revalidateData) {
        throw new Error("at least resetForm or revalidateData should be true");
      }
      emitter.emit("formWillRefresh", {
        resource,
        id,
        type: action,
      });

      if (resetForm && revalidateData) {
        clearStorage();
        const result = await refetch();
        processResult(result);
      } else if (resetForm) {
        form.reset(initialValues);
        clearStorage();
      } else if (revalidateData) {
        const result = await refetch();
        processResult(result);
      }
      if (import.meta.env.DEV) {
        await Promise.all([refetchTranslations(), refetchFormConfig()]);
      }

      setRefreshCount((prev) => prev + 1);
    },
    [
      refetchFormConfig,
      refetchTranslations,
      setRefreshCount,
      refetch,
      clearStorage,
      form,
      initialValues,
      processResult,
      resource,
      id,
      action,
    ],
  );
};
